import React, { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Table,
  Spinner,
  Input,
  Badge,
} from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import withAuth from "../withAuth"
import axios from "axios"
import { baseUrl } from "../../Network"
import { handleAuthError, newFormatDate } from "../../utils"
import PaginationControls from "../../components/Common/PaginationControls"
import FilterBarCollapsible from "../../components/Common/FilterBarCollapsible"
import StatusBadge from "../../components/Common/StatusBadge"
import ActionsDropdown from "../../components/Common/ActionsDropdown"
import ActivateAiTransferModal from "../../components/Common/ActivateAiTransferModal"
import { toast } from "react-toastify"

const SUBSCRIBED_FILTERS = [
  { value: "", label: "All" },
  { value: "yes", label: "Subscribed" },
  { value: "no", label: "Not subscribed" },
]

const accessBadgeColor = (key) => {
  if (key === "ai_app_only") return "primary"
  if (key === "normal_and_ai") return "info"
  if (key === "normal_platform") return "secondary"
  return "light"
}

const ManageAiClickedSubscribe = () => {
  document.title = "Clicked subscribe | RYD Admin"
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")
  const [subscribedFilter, setSubscribedFilter] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNextPage: false,
    hasPreviousPage: false,
  })
  const [actionLoadingId, setActionLoadingId] = useState(null)
  const [activateTransferModal, setActivateTransferModal] = useState(false)
  const [activateTransferParent, setActivateTransferParent] = useState(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const res = await axios.get(`${baseUrl}/admin/subscription/clicked-subscribe`, {
        params: {
          page: currentPage,
          limit: pageSize,
          search: search.trim() || undefined,
          subscribed: subscribedFilter || undefined,
        },
      })
      if (res?.data?.status) {
        setItems(Array.isArray(res.data.data?.items) ? res.data.data.items : [])
        setPagination(res.data.data?.pagination || pagination)
      }
    } catch (e) {
      handleAuthError(e)
    } finally {
      setLoading(false)
    }
  }, [currentPage, pageSize, search, subscribedFilter])

  useEffect(() => {
    load()
  }, [load])

  const handleSearch = (e) => {
    e.preventDefault()
    if (currentPage === 1) load()
    else setCurrentPage(1)
  }

  const openActivateTransferModal = (row) => {
    setActivateTransferParent(row || null)
    setActivateTransferModal(true)
  }

  const activateAiByTransfer = async (duration) => {
    if (!activateTransferParent?.parentId) return
    setActionLoadingId(activateTransferParent.parentId)
    try {
      const payload =
        duration?.days != null
          ? { days: Number(duration.days) }
          : { months: Number(duration?.months ?? duration) }
      const res = await axios.post(
        `${baseUrl}/admin/subscription/activate-transfer/${activateTransferParent.parentId}`,
        payload
      )
      if (res?.data?.status) {
        toast.success(res.data.message || "AI access activated")
        setActivateTransferModal(false)
        setActivateTransferParent(null)
        await load()
      } else {
        toast.error(res?.data?.message || "Failed")
      }
    } catch (e) {
      handleAuthError(e)
      toast.error(e?.response?.data?.message || "Failed to activate subscription")
    } finally {
      setActionLoadingId(null)
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Manage AI" breadcrumbItem="Clicked subscribe" />
        <Row className="mb-3">
          <Col>
            <p className="text-muted mb-0">
              Parents who tapped <strong>Proceed to subscribe</strong> in the AI Tutor flow (normal platform
              accounts). Shows whether they completed an AI subscription.
            </p>
          </Col>
        </Row>

        <FilterBarCollapsible title="Search & filter" defaultOpen>
          <form onSubmit={handleSearch} className="row g-2 align-items-end mb-3">
            <Col md={5}>
              <label className="form-label small text-muted">Search</label>
              <Input
                placeholder="Name, email, phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={4}>
              <label className="form-label small text-muted">Subscription status</label>
              <Input
                type="select"
                value={subscribedFilter}
                onChange={(e) => {
                  setSubscribedFilter(e.target.value)
                  setCurrentPage(1)
                }}
              >
                {SUBSCRIBED_FILTERS.map((f) => (
                  <option key={f.value || "all"} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <button type="submit" className="btn btn-primary w-100">
                Search
              </button>
            </Col>
          </form>
        </FilterBarCollapsible>

        <Row>
          <Col>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0">
                    Clicked proceed to subscribe
                    {!loading && (
                      <span className="text-muted fw-normal fs-6 ms-2">({pagination.totalItems})</span>
                    )}
                  </h5>
                  <Link to="/manage-parent" className="btn btn-sm btn-outline-secondary">
                    Manage parent (all)
                  </Link>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner />
                  </div>
                ) : items.length === 0 ? (
                  <p className="text-muted text-center py-4 mb-0">No parents have clicked proceed to subscribe yet.</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="table align-middle table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Parent</th>
                          <th>Access</th>
                          <th>Clicked subscribe</th>
                          <th>Subscribed</th>
                          <th>Subscribed date</th>
                          <th>Plan</th>
                          <th>Last updated</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((row) => {
                          const name = [row.firstName, row.lastName].filter(Boolean).join(" ") || "—"
                          return (
                            <tr key={row.parentId}>
                              <td>
                                <div className="fw-medium">{name}</div>
                                <small className="text-muted d-block">{row.email}</small>
                                {row.country && (
                                  <small className="text-muted d-block">{row.country}</small>
                                )}
                              </td>
                              <td>
                                <Badge color={accessBadgeColor(row.accessType)} className="font-size-11">
                                  {row.accessTypeLabel}
                                </Badge>
                              </td>
                              <td>
                                <StatusBadge variant="success">Yes</StatusBadge>
                              </td>
                              <td>
                                <StatusBadge variant={row.isSubscribed ? "success" : "danger"}>
                                  {row.isSubscribed ? "Yes" : "No"}
                                </StatusBadge>
                              </td>
                              <td>
                                {row.aiSubscribedAt ? newFormatDate(row.aiSubscribedAt) : "—"}
                              </td>
                              <td>{row.currentPlanKey || "—"}</td>
                              <td>{row.lastUpdatedAt ? newFormatDate(row.lastUpdatedAt) : "—"}</td>
                              <td className="text-end">
                                <ActionsDropdown
                                  loading={actionLoadingId === row.parentId}
                                  items={[
                                    {
                                      label: "Activate AI by transfer",
                                      icon: "mdi-bank-transfer",
                                      variant: "success",
                                      onClick: () => openActivateTransferModal(row),
                                    },
                                    {
                                      label: "View in AI parents",
                                      icon: "mdi-account",
                                      onClick: () =>
                                        window.open(
                                          `/ai/parents?search=${encodeURIComponent(row.email || "")}`,
                                          "_self"
                                        ),
                                    },
                                  ]}
                                />
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}

                {!loading && pagination.totalItems > 0 && (
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.totalItems}
                    pageSize={pageSize}
                    showAll={pageSize >= 10000}
                    paginatedDataLength={items.length}
                    handlePageChange={setCurrentPage}
                    handlePageSizeChange={(size) => {
                      if (size === "all") {
                        setPageSize(10000)
                      } else {
                        setPageSize(Number(size) || 20)
                      }
                      setCurrentPage(1)
                    }}
                    goToNextPage={() => {
                      if (pagination.hasNextPage) setCurrentPage((p) => p + 1)
                    }}
                    goToPreviousPage={() => {
                      if (pagination.hasPreviousPage) setCurrentPage((p) => Math.max(1, p - 1))
                    }}
                    canPreviousPage={pagination.hasPreviousPage}
                    canNextPage={pagination.hasNextPage}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <ActivateAiTransferModal
        isOpen={activateTransferModal}
        toggle={() => {
          if (actionLoadingId) return
          setActivateTransferModal(!activateTransferModal)
          if (activateTransferModal) setActivateTransferParent(null)
        }}
        parentLabel={
          activateTransferParent
            ? `${activateTransferParent.firstName || ""} ${activateTransferParent.lastName || ""}`.trim()
            : ""
        }
        loading={!!activateTransferParent && actionLoadingId === activateTransferParent.parentId}
        onSubmit={activateAiByTransfer}
      />
    </div>
  )
}

export default withAuth(ManageAiClickedSubscribe)
